import HttpApi from 'i18next-http-backend';
import React, { useEffect, useState } from 'react';
import i18next, { i18n } from 'i18next';
import { DEFAULT_LANGUAGE, SYSTEM_ISO_CODES } from '@loyalty/shared-common';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { STRING_NOT_TRANSLATED } from '@loyalty/translations';
import { translations as dataSearchUiTranslations } from '@qualifio/data-search-ui';
import { translations as ssoProxyTranslations } from '@qualifio/sso-proxy-ui';
import { defaultTranslations as uiCommonsTranslations } from '@qualifio/ui-commons';
import { Loader } from './shared/layout/Loader';
import { useLanguages } from './languages/LanguagesContext';
import { useSelectiveEffect } from './shared/use-selective-effect';

export interface TranslationProviderProps {
  children: React.ReactNode;
}

const additionnalResources = [
  uiCommonsTranslations,
  dataSearchUiTranslations,
  ssoProxyTranslations,
];

function addResourceRecursively(
  i18n: i18n,
  locale: string,
  namespace: string,
  value: any,
  path?: string,
) {
  if (typeof value === 'string') {
    if (!path) {
      throw new Error('Cannot add a string as a root namespace');
    }
    i18n.addResource(locale, namespace, path, value);
  } else {
    for (const key in value) {
      addResourceRecursively(i18n, locale, namespace, value[key], (path ? path + '.' : '') + key);
    }
  }
}

export function TranslationProvider({ children }: TranslationProviderProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const { currentLanguage } = useLanguages();
  const [i18nInstance, setI18nInstance] = useState<i18n>(undefined as any);

  useSelectiveEffect(
    () => {
      setError(undefined);
      const instance = i18next.createInstance();
      setI18nInstance(instance);
      instance
        .use(initReactI18next)
        .use(HttpApi)
        .init({
          debug: false,
          lng: currentLanguage.isoCode,
          fallbackLng: DEFAULT_LANGUAGE,
          supportedLngs: SYSTEM_ISO_CODES,
          keySeparator: false,
          interpolation: {
            escapeValue: false,
          },
          backend: {
            loadPath: `${BASE_PATH}/assets/locales/{{lng}}/{{ns}}.json?cache=${BUILD_VERSION}`,
            parse(
              data: string,
              languages?: string | string[],
              namespaces?: string | string[],
            ): { [p: string]: string } {
              const json = JSON.parse(data);
              // remove untranslated strings so they are detected as missing
              Object.keys(json).forEach(key => {
                if (json[key] === STRING_NOT_TRANSLATED) {
                  delete json[key];
                }
              });
              return json;
            },
          },
          saveMissing: true,
          updateMissing: true,
          missingKeyHandler: (lngs, ns, key, fallbackValue) => {
            console.warn(`Missing "${lngs.join('')}" translation for ns "${ns}" and key "${key}"`);
          },
          react: {
            useSuspense: false,
          },
        })
        .then(() => {
          for (const resources of additionnalResources) {
            for (const locale in resources) {
              for (const namespace in (resources as Record<string, any>)[locale]) {
                addResourceRecursively(
                  instance,
                  locale,
                  namespace,
                  (resources as Record<string, any>)[locale][namespace],
                );
              }
            }
          }
        })
        .catch(setError)
        .finally(() => {
          setLoading(false);
        });
    },
    [currentLanguage.isoCode],
    [],
  );

  useEffect(() => {
    if (i18nInstance && i18nInstance.language !== currentLanguage.isoCode) {
      i18nInstance.changeLanguage(currentLanguage.isoCode).catch(setError);
    }
  }, [currentLanguage.isoCode, i18nInstance]);

  return loading ? (
    <Loader />
  ) : error ? (
    // TODO proper error page
    <>{error}</>
  ) : (
    <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>
  );
}
